<script setup>
import {
  watch,
  onUnmounted,
} from 'vue';

import { storeToRefs } from 'pinia';

import Toast from 'primevue/toast';
import DynamicDialog from 'primevue/dynamicdialog';
import ConfirmDialog from 'primevue/confirmdialog';

import VersionCheckToast from '@/components/VersionCheckToast.vue';
import useThemeSwitcher from '@/composables/useThemeSwitcher';
import usePusher from '@/composables/usePusher';
import ConduitEventsService from '@/services/conduit';

import useAuthStore from '@/stores/auth';

const pusher = usePusher();
const { currentTeamId, userId } = storeToRefs(useAuthStore());

const { themePreference, applyTheme } = useThemeSwitcher();

watch(themePreference, (theme) => applyTheme(theme));

// The Pusher lifecycle lives above the team-keyed RouterView. If it lived in
// BaseLoggedInView, changing teams would tear the layout (and the connection)
// down and rebuild it in the same tick, racing the new connection.
watch(currentTeamId, (newVal, oldVal) => {
  if (oldVal) {
    pusher.unsubscribeFromAdobeNotifications(oldVal);
    pusher.unsubscribeFromUserChannel(oldVal);
  }

  if (!newVal) {
    // Team cleared (e.g. logout): drop the team-scoped connection too.
    pusher.disconnectAdobe();
    return;
  }

  // Rebuilds the authed Adobe connection for the newly selected team and keeps
  // its owner-scoped notification channel subscribed globally.
  pusher.init(newVal);
  pusher.subscribeToUserChannel(newVal);
  pusher.subscribeToAdobeNotifications(newVal);
}, { immediate: true });

// Conduit events are user-scoped; initialize once per signed-in user.
watch(userId, (id) => {
  if (id) ConduitEventsService.init(id);
}, { immediate: true });

onUnmounted(() => pusher.disconnect());
</script>

<template>
  <RouterView :key="currentTeamId" />
  <Toast />
  <DynamicDialog />
  <ConfirmDialog />
  <VersionCheckToast />
</template>

<style>
.page {
  margin:0;
}
</style>
